'use client';

import { FormEvent, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/components/AuthProvider';
import { UpgradeNudge } from '@/components/PremiumGate';
import { getToken, isProUser } from '@/lib/auth';

interface CommunityPlantPhotoSubmissionProps {
  plantId: string;
  plantName: string;
}

function CameraIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 5.5C2 4.67 2.67 4 3.5 4H5L6.25 2.5H9.75L11 4H12.5C13.33 4 14 4.67 14 5.5V12C14 12.83 13.33 13.5 12.5 13.5H3.5C2.67 13.5 2 12.83 2 12V5.5Z" />
      <circle cx="8" cy="8.75" r="2.25" />
    </svg>
  );
}

export default function CommunityPlantPhotoSubmission({
  plantId,
  plantName,
}: CommunityPlantPhotoSubmissionProps) {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [varietyOrType, setVarietyOrType] = useState('');
  const [description, setDescription] = useState('');
  const [attribution, setAttribution] = useState('');
  const [consentToUse, setConsentToUse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUpgradeNudge, setShowUpgradeNudge] = useState(false);

  const isPro = user ? isProUser(user) : false;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!photo) {
      setError('Add a photo before submitting.');
      return;
    }
    if (!varietyOrType.trim()) {
      setError('Add the variety or type shown in the photo.');
      return;
    }
    if (!consentToUse) {
      setError('Consent is required before submitting.');
      return;
    }

    const formData = new FormData();
    formData.set('plantId', plantId);
    formData.set('photo', photo);
    formData.set('varietyOrType', varietyOrType.trim());
    formData.set('description', description.trim());
    formData.set('attribution', attribution.trim());
    formData.set('consentToUse', 'true');

    setSubmitting(true);
    try {
      const token = getToken();
      const response = await fetch('/api/plant-photos', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
      });
      const data = await response.json() as { success?: boolean; error?: string };
      if (!response.ok || !data.success) {
        throw new Error(data.error ?? 'Photo submission failed.');
      }

      setPhoto(null);
      setVarietyOrType('');
      setDescription('');
      setAttribution('');
      setConsentToUse(false);
      setOpen(false);
      setSuccess('Photo submitted for review. It will appear after approval.');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Photo submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <section className="bg-white rounded-xl border border-sand p-6">
        <p className="font-body text-[14px] text-sage">Loading photo submission…</p>
      </section>
    );
  }

  if (!user) {
    return (
      <section className="bg-white rounded-xl border border-sand p-6 space-y-3">
        <div className="flex items-center gap-2 text-sage">
          <CameraIcon />
          <h2 className="font-heading text-[20px] text-warm-brown">Share a Photo</h2>
        </div>
        <p className="font-body text-[14px] text-sage">
          Sign in with Garden Pro to submit plant photos for review.
        </p>
        <Link
          href="/login"
          className="inline-flex rounded-lg bg-deep-green px-4 py-2 font-body text-[14px] font-semibold text-white hover:opacity-90 transition-opacity"
        >
          Sign in
        </Link>
      </section>
    );
  }

  if (!isPro) {
    return (
      <section className="bg-white rounded-xl border border-sand p-6 space-y-3">
        <div className="flex items-center gap-2 text-sage">
          <CameraIcon />
          <h2 className="font-heading text-[20px] text-warm-brown">Share a Photo</h2>
        </div>
        <p className="font-body text-[14px] text-sage">
          Approved photos are visible to everyone after review.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowUpgradeNudge(true)}
            className="inline-flex rounded-lg border border-sand bg-cream px-4 py-2 font-body text-[14px] font-semibold text-sage hover:border-sage transition-colors"
          >
            Add Your Photo
          </button>
        </div>
        {showUpgradeNudge && (
          <UpgradeNudge
            title="Share your garden with the community"
            body="Pro members can submit plant photos, contribute to the library, and get featured."
          />
        )}
      </section>
    );
  }

  return (
    <section className="bg-white rounded-xl border border-sand p-6 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-[20px] text-warm-brown">Share a Photo</h2>
          <p className="font-body text-[14px] text-sage mt-1">
            Submit a {plantName} photo for moderator review.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(current => !current);
            setError('');
            setSuccess('');
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-deep-green px-4 py-2 font-body text-[14px] font-semibold text-white hover:opacity-90 transition-opacity"
        >
          <CameraIcon />
          Add Your Photo
        </button>
      </div>

      {success && (
        <p className="rounded-lg border border-[#C5D9B8] bg-light-sage px-4 py-3 font-body text-[14px] text-deep-green">
          {success}
        </p>
      )}

      {open && (
        <form onSubmit={handleSubmit} className="space-y-4 border-t border-sand pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="font-body text-[13px] font-semibold text-dark-text">Photo</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={event => setPhoto(event.target.files?.[0] ?? null)}
                className="block w-full font-body text-[13px] text-sage file:mr-3 file:rounded-lg file:border-0 file:bg-light-sage file:px-3 file:py-2 file:font-body file:text-[13px] file:font-semibold file:text-deep-green"
              />
            </label>
            <label className="space-y-1.5">
              <span className="font-body text-[13px] font-semibold text-dark-text">Variety / Type</span>
              <input
                value={varietyOrType}
                onChange={event => setVarietyOrType(event.target.value)}
                placeholder="Roma tomato, red flower, seedling…"
                className="w-full rounded-lg border border-sand bg-white px-3 py-2 font-body text-[14px] text-dark-text focus:outline-none focus:border-sage"
              />
            </label>
          </div>

          <label className="block space-y-1.5">
            <span className="font-body text-[13px] font-semibold text-dark-text">Short description</span>
            <textarea
              value={description}
              onChange={event => setDescription(event.target.value)}
              rows={3}
              placeholder="Optional notes about what the photo shows."
              className="w-full rounded-lg border border-sand bg-white px-3 py-2 font-body text-[14px] text-dark-text focus:outline-none focus:border-sage resize-y"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="font-body text-[13px] font-semibold text-dark-text">Name or handle for credit</span>
            <input
              value={attribution}
              onChange={event => setAttribution(event.target.value)}
              placeholder="Your name or @handle"
              className="w-full rounded-lg border border-sand bg-white px-3 py-2 font-body text-[14px] text-dark-text focus:outline-none focus:border-sage"
            />
          </label>

          <label className="flex gap-3 rounded-lg border border-sand bg-light-sage px-4 py-3">
            <input
              type="checkbox"
              checked={consentToUse}
              onChange={event => setConsentToUse(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-sand text-deep-green focus:ring-deep-green"
            />
            <span className="font-body text-[13px] text-dark-text leading-relaxed">
              I agree that my photo may be displayed in the app and used by Willowbottom (including social media).
            </span>
          </label>

          {error && (
            <p className="font-body text-[13px] text-alert-red">{error}</p>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-deep-green px-5 py-2.5 font-body text-[14px] font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit for Review'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError('');
              }}
              className="rounded-lg border border-sand px-5 py-2.5 font-body text-[14px] font-semibold text-sage hover:bg-cream transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
